import React, { useCallback, useEffect, useRef, useState } from 'react';
import appNoticeService from '../../services/appNoticeService';
import { useUser } from '../../context/UserContext';
import { useDialog } from '../../context/DialogContext';
import { useAdminShell } from '../admin/shell/AdminShellContext';
import { shortDate } from '../admin/shell';
import { SoftField, SoftText } from '../broadcast/ui';
import { StudioCard, Segmented, ChecksList, RingCta } from '../broadcast/studioUi';
import ManualNotice from './ManualNotice';
import AudienceEditor, { BLANK_AUDIENCE, targetLabel } from './AudienceEditor';
import ImageField from './ImageField';
import LinkPicker from './LinkPicker';
import { NoticesHero, HeroButton, useNoticesLayout, FilterPills, ListRow, ListEmpty, PreviewCard, counterOf, ActiveSwitch, ChoiceChip } from './noticesUi';

/**
 * APP NOTICES › SHEETS — the admin composer for in-app announcements (a bottom sheet on
 * phones, a card on desktop), built to design_handoff_app_notices. Deliberately NOT called
 * "broadcasts": that word belongs to the email newsletter composer.
 *
 * LOOK (accent, hero) · WORDS (label, headline, body, button, GOES TO) · WHO SEES IT (the
 * shared AudienceEditor) · WHEN + PUBLISH (window, ACTIVE, the checks, the ring CTA), beside a
 * preview that renders the real `ManualNotice` on a phone or a desktop frame. The list of
 * sheets sits under the composer.
 *
 * Gates: the composer + preview render with `app_notices` · create, or with `edit` while a row
 * is open for editing. Row EDIT needs `edit`; DELETE needs `delete`.
 */
const ACCENTS = [
  { value: '#FF33C6', label: 'HOT PINK' },
  { value: '#22ECFB', label: 'CYAN' },
  { value: '#B5B1FF', label: 'LAVENDER' },
  { value: '#FAE1B4', label: 'CREAM' },
  { value: '#FCAA6D', label: 'PEACH' },
];
// Stored values the chip row no longer offers still need a name in the list.
export const ACCENT_LABELS = {
  ...Object.fromEntries(ACCENTS.map((a) => [a.value.toLowerCase(), a.label])),
  '#ffcce6': 'DREAM PINK', '#a8d8ea': 'NINTENDO BLUE', '#c6b8ff': 'LAVENDER FOG', '#fff8ea': 'CREAM', '#00ffff': 'HYPER CYAN',
};
export const accentLabel = (v) => ACCENT_LABELS[String(v || '').toLowerCase()] || String(v || '—').toUpperCase();

const HERO_KINDS = [{ value: 'plate', label: 'PLATE' }, { value: 'color', label: 'COLOR' }, { value: 'image', label: 'IMAGE' }];
const HERO_NOTE = { plate: 'Dark panel with the label set large in the accent.', color: 'A flat block of color.', image: 'A photo across the top.' };
const SWATCHES = ['#FD199F', '#FF33C6', '#B5B1FF', '#127BFD', '#22ECFB', '#FAE1B4', '#FCAA6D', '#0A0A0A'];
const DEFAULT_HERO_COLOR = '#FF33C6';

const BLANK = {
  eyebrow: '',
  headline: '',
  body: '',
  heroKind: 'plate',
  heroValue: '',
  accent: '#22ECFB',
  primaryLabel: '',
  primaryHref: '',
  secondaryLabel: 'NOT NOW',
  publishStart: '',
  publishEnd: '',
  isActive: true,
  target: { audience: BLANK_AUDIENCE, cities: [] },
  legacyCity: null,
};

const isLive = (n) => n.isActive && (!n.publishEnd || new Date(n.publishEnd).getTime() > Date.now());
const windowOf = (n) => (n.publishStart || n.publishEnd ? `${n.publishStart ? shortDate(n.publishStart) : '…'} → ${n.publishEnd ? shortDate(n.publishEnd) : '…'}` : 'OPEN');
const peopleNoun = (n) => `${Number(n || 0).toLocaleString()} ${n === 1 ? 'PERSON' : 'PEOPLE'}`;

/** A row saved before audiences existed still carries the three old columns. */
function targetFromRow(n) {
  const audience = { ...BLANK_AUDIENCE, ...(n.audience || {}) };
  if (audience.scopes.length === 0) {
    if (n.targetHostOnly) audience.scopes = [...audience.scopes, 'hosts'];
    if (n.targetClubId) { audience.scopes = [...audience.scopes, 'clubs']; audience.clubIds = [n.targetClubId]; }
  }
  return { audience, cities: n.cities || [] };
}

/** The phone (390×780, sheet docked bottom) or desktop (800×640, 440 card) frame around the real sheet. */
function SheetFrame({ view, notice }) {
  const [open, setOpen] = useState(true);
  const desk = view === 'desktop';
  return (
    <div className="relative flex justify-center overflow-hidden" style={{ width: desk ? 800 : 390, height: desk ? 640 : 780, alignItems: desk ? 'center' : 'flex-end', background: 'radial-gradient(120% 80% at 30% 20%, #3A2A4F 0%, #111 60%)' }}>
      <span className="absolute inset-0" style={{ background: 'rgba(0,0,0,.62)' }} aria-hidden="true" />
      {open ? (
        <div className="relative flex flex-col text-white" style={{ width: desk ? 440 : '100%', margin: desk ? 24 : 0, boxSizing: 'border-box', borderRadius: desk ? 28 : '28px 28px 0 0', background: '#0A0A0A' }}>
          {!desk && <span className="self-center rounded-full" style={{ width: 40, height: 4, marginTop: 12, background: '#2E2E2E' }} aria-hidden="true" />}
          <ManualNotice notice={notice} onPrimary={() => {}} onSecondary={() => setOpen(false)} />
        </div>
      ) : (
        <div className="relative flex flex-col items-center self-center text-white" style={{ gap: 12 }}>
          <span className="font-mono text-[10px] font-extrabold tracking-[0.16em]">SNOOZED · NO SHEETS FOR 7 DAYS</span>
          <button type="button" onClick={() => setOpen(true)} className="rounded-full bg-white font-mono text-[10px] font-extrabold tracking-[0.14em] text-black" style={{ height: 40, padding: '0 18px' }}>SHOW AGAIN</button>
        </div>
      )}
    </div>
  );
}

export default function AppNoticeComposer({ tabs }) {
  const { can } = useUser();
  const { showConfirm } = useDialog();
  const { toast } = useAdminShell();
  const canCreate = can('app_notices', 'create');
  const canEdit = can('app_notices', 'edit');
  const canDelete = can('app_notices', 'delete');

  const rootRef = useRef(null);
  const finishRef = useRef(null);
  const { wide, narrow, previewCol, cols, twoCol } = useNoticesLayout(rootRef);

  const [notices, setNotices] = useState(null);
  const [filter, setFilter] = useState('live');
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState('');
  const [previewView, setPreviewView] = useState('phone');
  const [recentUploads, setRecentUploads] = useState([]);
  const [linkRes, setLinkRes] = useState({ ok: true, pending: false, kind: 'PAGE', label: 'Home', path: '/' });
  const [reach, setReach] = useState(null);

  const load = useCallback(async () => {
    const r = await appNoticeService.list();
    setNotices(r.success ? (r.data || []) : []);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!flash) return undefined; const t = setTimeout(() => setFlash(''), 3000); return () => clearTimeout(t); }, [flash]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => { setEditingId(null); setForm(BLANK); setError(null); };
  const onResolved = useCallback((res) => setLinkRes(res), []);

  const heroOk = form.heroKind !== 'image' || !!form.heroValue;
  const windowOk = !form.publishStart || !form.publishEnd || form.publishEnd > form.publishStart;
  const checks = [
    { key: 'words', label: 'Label, headline and body', ok: !!(form.eyebrow.trim() && form.headline.trim() && form.body.trim()) },
    { key: 'button', label: 'Button label', ok: !!form.primaryLabel.trim() },
    { key: 'link', label: 'Button link goes somewhere real', ok: !!linkRes.ok && !linkRes.pending },
    ...(form.heroKind === 'image' ? [{ key: 'hero', label: 'Hero image picked', ok: heroOk }] : []),
    { key: 'window', label: 'Ends after it starts', ok: windowOk },
    { key: 'reach', label: 'Reaches at least one person', ok: (reach || 0) > 0 },
  ];
  const allOk = checks.every((c) => c.ok);

  const save = async () => {
    if (!allOk || saving) return;
    setSaving(true);
    setError(null);
    const { target, legacyCity, ...rest } = form;
    const payload = {
      ...rest,
      eyebrow: form.eyebrow.toUpperCase(),
      primaryLabel: form.primaryLabel.toUpperCase(),
      primaryHref: linkRes.path || form.primaryHref || '/',
      heroValue: form.heroValue || null,
      publishStart: form.publishStart ? new Date(form.publishStart).toISOString() : null,
      publishEnd: form.publishEnd ? new Date(form.publishEnd).toISOString() : null,
      audience: { scopes: target.audience.scopes, clubIds: target.audience.clubIds, userIds: target.audience.userIds, newWithinDays: target.audience.newWithinDays },
      cities: target.cities,
      // The old text match survives only until a real city is picked.
      targetCity: target.cities.length ? null : legacyCity,
      targetHostOnly: false,
      targetClubId: null,
    };
    const r = editingId ? await appNoticeService.update(editingId, payload) : await appNoticeService.create(payload);
    setSaving(false);
    if (r.success) {
      const word = editingId ? 'SAVED' : 'PUBLISHED';
      setFlash(word);
      toast(editingId ? 'CHANGES SAVED' : `PUBLISHED TO ${peopleNoun(reach)}`);
      setFilter(form.isActive ? 'live' : 'past');
      reset();
      load();
    } else setError(r.error || 'Could not save this notice');
  };

  const edit = (n) => {
    setEditingId(n.id);
    setError(null);
    setFlash('');
    setForm({
      ...BLANK,
      ...n,
      heroValue: n.heroValue || '',
      primaryHref: n.primaryHref === '/' ? '' : (n.primaryHref || ''),
      publishStart: n.publishStart ? n.publishStart.slice(0, 16) : '',
      publishEnd: n.publishEnd ? n.publishEnd.slice(0, 16) : '',
      target: targetFromRow(n),
      legacyCity: (n.cities || []).length === 0 && n.targetCity ? n.targetCity : null,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const remove = async (n) => {
    const ok = await showConfirm({ title: 'DELETE NOTICE', message: 'Removes it for everyone, including anyone who has not seen it yet.', confirmLabel: 'DELETE', variant: 'danger', theme: 'admin' });
    if (!ok) return;
    await appNoticeService.remove(n.id);
    if (editingId === n.id) reset();
    toast('NOTICE DELETED');
    load();
  };

  const setHeroKind = (k) => {
    if (k === form.heroKind) return;
    setForm((f) => ({ ...f, heroKind: k, heroValue: k === 'color' ? DEFAULT_HERO_COLOR : '' }));
  };
  const setHeroColor = (raw) => {
    let v = (raw || '').trim();
    if (v && !v.startsWith('#')) v = `#${v}`;
    set('heroValue', v.slice(0, 7));
  };
  const goFinish = () => finishRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const showComposer = canCreate || (editingId && canEdit);
  const rows = (notices || []).filter((n) => (filter === 'live' ? isLive(n) : !isLive(n)));
  const total = (notices || []).length;
  const library = (notices || []).filter((n) => n.heroKind === 'image' && n.heroValue).map((n) => n.heroValue);
  const status = flash || (editingId ? 'EDITING' : 'NEW');

  const previewNotice = {
    ...form,
    heroValue: form.heroValue || '',
    eyebrow: form.eyebrow || 'ANNOUNCEMENT',
    headline: form.headline || 'Your headline here.',
    body: form.body || 'One or two sentences of body copy.',
    primaryLabel: form.primaryLabel || 'PRIMARY ACTION',
  };
  const heroColor = form.heroKind === 'color' ? (form.heroValue || DEFAULT_HERO_COLOR) : DEFAULT_HERO_COLOR;

  const listCard = (
    <StudioCard title="SHEETS" narrow={narrow} gap={14} testId="sheets-list"
      right={<FilterPills options={[{ value: 'live', label: 'LIVE' }, { value: 'past', label: 'PAST' }]} value={filter} onChange={setFilter} />}>
      {notices == null && <ListEmpty>LOADING…</ListEmpty>}
      {rows.map((n) => (
        <ListRow key={n.id} testId={`sheet-row-${n.id}`} editing={editingId === n.id}
          thumb={n.heroKind === 'image' && n.heroValue ? { image: n.heroValue } : n.heroKind === 'color' ? { color: n.heroValue || n.accent } : { color: '#161616', text: (n.eyebrow || '').split(/\s*·\s*/)[0], ink: n.accent }}
          title={n.headline}
          meta={`${targetLabel(n)} · ${windowOf(n)}`}
          stats={[{ k: 'SEEN', v: (n.seenCount ?? 0).toLocaleString() }, { k: 'ACTED', v: (n.actedCount ?? 0).toLocaleString() }]}
          pill={isLive(n) ? { label: 'LIVE', bg: '#22C55E', fg: '#000' } : { label: 'ENDED', bg: '#E5E5E5', fg: '#000' }}
          actions={[
            canEdit && { label: 'EDIT', onClick: () => edit(n) },
            canDelete && { label: 'DELETE', variant: 'danger', onClick: () => remove(n) },
          ].filter(Boolean)}
        />
      ))}
      {notices != null && rows.length === 0 && <ListEmpty>{filter === 'live' ? 'NOTHING LIVE RIGHT NOW.' : 'NOTHING HERE YET.'}</ListEmpty>}
      <span className="font-mono text-[10px] tracking-[0.12em] text-[#9CA3AF]">{total} {total === 1 ? 'SHEET' : 'SHEETS'}</span>
    </StudioCard>
  );

  return (
    <div ref={rootRef} className="flex flex-col" style={{ gap: 14 }}>
      <NoticesHero status={status} subLine="IN-APP SHEETS · NOT EMAIL" actions={showComposer ? (
        <>
          <HeroButton onClick={reset}>{editingId ? 'CANCEL EDIT' : 'CLEAR'}</HeroButton>
          <HeroButton variant="white" onClick={goFinish}>REVIEW &amp; PUBLISH</HeroButton>
        </>
      ) : null} />
      {tabs}

      {showComposer ? (
        <div className="grid items-start" style={{ gridTemplateColumns: cols, gap: 14 }}>
          <div className="flex min-w-0 flex-col" style={{ gap: 14 }}>
            <StudioCard title="LOOK" note="ACCENT COLORS THE LABEL RING AND BUTTON" narrow={narrow} gap={20}>
              <div className="flex flex-col" style={{ gap: 10 }}>
                <span className="font-mono text-[10px] font-bold tracking-[0.16em] text-[#6B7280]">ACCENT</span>
                <div className="flex flex-wrap" style={{ gap: 8 }} role="radiogroup" aria-label="Accent">
                  {ACCENTS.map((a) => (
                    <ChoiceChip key={a.value} on={form.accent.toLowerCase() === a.value.toLowerCase()} onClick={() => set('accent', a.value)} ariaLabel={`Accent ${a.label}`}
                      style={{ height: 48, padding: '0 18px 0 7px', gap: 10, fontSize: 10, letterSpacing: '.14em' }}>
                      <span className="rounded-full" style={{ width: 34, height: 34, background: a.value }} aria-hidden="true" />{a.label}
                    </ChoiceChip>
                  ))}
                </div>
              </div>
              <div className="flex flex-col" style={{ gap: 10 }}>
                <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
                  <Segmented label="HERO" track="#F2F2F2" options={HERO_KINDS} value={form.heroKind} onChange={setHeroKind} />
                  <span className="font-mono text-[10px] text-[#6B7280]">{HERO_NOTE[form.heroKind]}</span>
                </div>
                {form.heroKind === 'color' && (
                  <div className="flex flex-wrap items-center" style={{ gap: 12, padding: 14, borderRadius: 22, background: '#F5F5F5' }}>
                    <label className="relative flex-shrink-0 cursor-pointer rounded-full" style={{ width: 44, height: 44, background: heroColor, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.12)' }}>
                      <input type="color" value={/^#[0-9a-f]{6}$/i.test(heroColor) ? heroColor : DEFAULT_HERO_COLOR} onChange={(e) => setHeroColor(e.target.value)} aria-label="Hero colour" className="absolute inset-0 h-full w-full cursor-pointer opacity-0" />
                    </label>
                    <input value={heroColor} onChange={(e) => setHeroColor(e.target.value)} aria-label="Hero colour hex" className="rounded-[12px] border-2 border-transparent bg-white font-mono text-[11px] uppercase focus:border-black focus:outline-none focus:ring-0" style={{ width: 110, height: 38, padding: '0 12px', boxSizing: 'border-box' }} />
                    <div className="flex flex-wrap" style={{ gap: 6 }}>
                      {SWATCHES.map((c) => (
                        <button key={c} type="button" title={c} aria-label={`Hero colour ${c}`} onClick={() => set('heroValue', c)} className="rounded-full border-0" style={{ width: 26, height: 26, background: c, boxShadow: heroColor.toLowerCase() === c.toLowerCase() ? '0 0 0 2px #fff, 0 0 0 4px #0A0A0A' : 'inset 0 0 0 1px rgba(0,0,0,.12)' }} />
                      ))}
                    </div>
                  </div>
                )}
                {form.heroKind === 'image' && (
                  <ImageField value={form.heroValue} onChange={(url) => set('heroValue', url)} library={library} recent={recentUploads} onUploaded={(url) => setRecentUploads((r) => [url, ...r.filter((x) => x !== url)])}
                    help="JPG or PNG, 1200px+ wide. The middle 150px shows across the top of the sheet." />
                )}
              </div>
            </StudioCard>

            <StudioCard title="WORDS" narrow={narrow} gap={16}>
              <div className="grid" style={{ gridTemplateColumns: twoCol, gap: 14 }}>
                <SoftField label="LABEL" required right={counterOf(form.eyebrow, 34).text} rightColor={counterOf(form.eyebrow, 34).color} htmlFor="sheet-label">
                  <SoftText id="sheet-label" caps maxLength={34} value={form.eyebrow} onChange={(v) => set('eyebrow', v)} placeholder="ANNOUNCEMENT · PENTICTON" />
                </SoftField>
                <SoftField label="HEADLINE" required right={counterOf(form.headline, 60).text} rightColor={counterOf(form.headline, 60).color} htmlFor="sheet-headline">
                  <SoftText id="sheet-headline" maxLength={60} value={form.headline} onChange={(v) => set('headline', v)} placeholder="The City Portal is open in Penticton." />
                </SoftField>
              </div>
              <SoftField label="BODY" required right={counterOf(form.body, 180).text} rightColor={counterOf(form.body, 180).color} htmlFor="sheet-body">
                <SoftText id="sheet-body" rows={3} maxLength={180} value={form.body} onChange={(v) => set('body', v)} placeholder="One or two sentences. Say what changed and where." />
              </SoftField>
              <div className="grid" style={{ gridTemplateColumns: twoCol, gap: 14 }}>
                <SoftField label="BUTTON" required right={counterOf(form.primaryLabel, 40).text} rightColor={counterOf(form.primaryLabel, 40).color} htmlFor="sheet-button">
                  <SoftText id="sheet-button" caps maxLength={40} value={form.primaryLabel} onChange={(v) => set('primaryLabel', v)} placeholder="ENTER THE CITY PORTAL" />
                </SoftField>
                <div className="flex min-w-0 flex-col" style={{ gap: 8 }}>
                  <span className="flex justify-between font-mono text-[10px] font-bold tracking-[0.16em] text-[#6B7280]" style={{ gap: 8 }}><span className="whitespace-nowrap">GOES TO</span><span className="whitespace-nowrap text-[#9CA3AF]">PAGE OR LINK</span></span>
                  <LinkPicker id="sheet-link" mode="sheet" value={form.primaryHref} onChange={(v) => set('primaryHref', v)} onResolved={onResolved} />
                </div>
              </div>
              <span className="font-mono text-[10px] leading-[1.6] text-[#6B7280]">Every sheet ends with NOT NOW. Dismissing any sheet starts a 7-day quiet period for that person.</span>
            </StudioCard>

            <AudienceEditor value={form.target} onChange={(t) => set('target', t)} legacyCity={form.legacyCity} onReach={setReach} narrow={narrow} />

            <StudioCard title="WHEN + PUBLISH" note="BLANK DATES = LIVE UNTIL TURNED OFF" narrow={narrow} innerRef={finishRef} testId="finish-card">
              <div className="grid" style={{ gridTemplateColumns: twoCol, gap: 14 }}>
                <SoftField label="SHOW FROM" right="BLANK = NOW" htmlFor="sheet-start">
                  <input id="sheet-start" type="datetime-local" value={form.publishStart} onChange={(e) => set('publishStart', e.target.value)} className="w-full rounded-full border-2 border-transparent bg-[#F5F5F5] font-mono text-[12px] focus:border-black focus:outline-none focus:ring-0" style={{ height: 46, padding: '0 16px', boxSizing: 'border-box' }} />
                </SoftField>
                <SoftField label="SHOW UNTIL" right="BLANK = UNTIL OFF" htmlFor="sheet-end">
                  <input id="sheet-end" type="datetime-local" value={form.publishEnd} onChange={(e) => set('publishEnd', e.target.value)} className="w-full rounded-full border-2 border-transparent bg-[#F5F5F5] font-mono text-[12px] focus:border-black focus:outline-none focus:ring-0" style={{ height: 46, padding: '0 16px', boxSizing: 'border-box' }} />
                </SoftField>
              </div>
              <ActiveSwitch on={form.isActive} onToggle={() => set('isActive', !form.isActive)} />
              <ChecksList checks={checks} />
              {error && <div className="font-mono text-[11px] leading-[1.6] text-[#B91C1C]" style={{ padding: '10px 14px', borderRadius: 14, background: '#FEF2F2' }} role="alert">{error}</div>}
              <RingCta ready={allOk && !saving} onClick={save} testId="publish-sheet">
                {saving ? 'SAVING…' : editingId ? 'SAVE CHANGES' : `PUBLISH TO ${peopleNoun(reach)}`}
              </RingCta>
            </StudioCard>

            {wide && listCard}
          </div>

          <PreviewCard views={[{ value: 'phone', label: 'PHONE' }, { value: 'desktop', label: 'DESKTOP' }]} view={previewView} onView={setPreviewView} previewCol={previewCol} canW={previewView === 'desktop' ? 800 : 390} sticky={wide}>
            <SheetFrame key={previewView} view={previewView} notice={previewNotice} />
          </PreviewCard>
          {!wide && listCard}
        </div>
      ) : listCard}
    </div>
  );
}
